import React, { useEffect, useState } from "react";
import { Formik, Form, Field, FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import { Input, InputWrapper } from "../composites";
import { Grid, Box, Flex } from "@chakra-ui/react";
import _ from "lodash";
import { Button } from "@chakra-ui/react";
import {
  changePassword,
  createTransaction,
  createUser,
  getAccount,
  updateUser
} from "../../services/crm.service";
import { useShowToastHook } from "../../hooks/show-toast-hook";
import ErrorBoundary from "../ErrorBoundary";
import { useQuery, useQueryClient } from "react-query";
import { Skeleton } from "@chakra-ui/react";
import { setUser } from "../../services/login.service";
import { Select } from "@chakra-ui/react";
import { colors } from "../../config/colors";

const TYPE = {
  DEPOSIT: "Deposit",
  WITHDRAW: "Withdraw",
  CREDIT_IN: "Credit In",
  CREDIT_OUT: "Credit Out"
};

//f_name: f_name,
//     m_name: m_name,
//    l_name: l_name,
//group f_name m_name l_name  master_password investor_password  email phone company country zipcode state city address
const userSchema = Yup.object().shape({
  login: Yup.number().required("Login ID is required"),
  f_name: Yup.string().required("Required"),
  m_name: Yup.string(),
  l_name: Yup.string(),
  color: Yup.number(),
  email: Yup.string().matches(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  ),
  phone: Yup.string(),
  company: Yup.string(),
  country: Yup.string(),
  zipcode: Yup.string(),
  state: Yup.string(),
  city: Yup.string(),
  address: Yup.string()
});

const EditUserFrom = ({ login }: { login: string }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    fetchAccount();
  }, []);

  const fetchAccount = async () => {
    setIsLoading(true);

    try {
      const result = await getAccount(login);

      if (!_.isEmpty(result?.data?.data)) {
        setData(result?.data?.data);
      }
    } catch (error) {}

    setIsLoading(false);
  };

  if (isLoading)
    return (
      <>
        <Skeleton minHeight="500px" width="100%" />
      </>
    );

  let user = data ?? null;

  if (!user) return null;

  return (
    <>
      <FormEditUser
        login={parseInt(login)}
        f_name={user?.firstName}
        m_name={user?.middleName}
        l_name={user?.lastName}
        color={user?.color}
        {...user}
      />
    </>
  );
};

export default EditUserFrom;

const FormEditUser = ({
  login,
  f_name,
  m_name,
  l_name,
  color,
  email,
  phone,
  company,
  country,
  zipcode,
  state,
  city,
  address
}: {
  login: number;
  f_name: string;
  m_name: string;
  l_name: string;
  color?: number;
  email?: string;
  phone?: string;
  company?: string;
  country?: string;
  zipcode?: string;
  state?: string;
  city?: string;
  address?: string;
}) => {
  const { showError, showSuccess } = useShowToastHook();
  const qc = useQueryClient();

  const formik = useFormik({
    initialValues: {
      login: login,
      f_name: f_name,
      m_name: m_name,
      l_name: l_name,
      color: color,
      email: email,
      phone: phone,
      company: company,
      country: country,
      zipcode: zipcode,
      state: state,
      city: city,
      address: address
    },
    validationSchema: userSchema,
    onSubmit: async (values, actions) => {
      actions.setSubmitting(true);

      try {
        const result = await updateUser(values);

        if (result?.data?.status) {
          showSuccess("User updated Successfully.");
          setTimeout(() => {
            window.location.href = "/accounts";
          }, 1500);
          //actions.resetForm();
        } else {
          showError(
            result?.data?.message ??
              "Something went wrong, please try again later."
          );
        }
      } catch (error) {
        showError("Something went wrong, please try again later.");
      }

      actions.setSubmitting(false);
    }
  });
  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Grid
          my={6}
          gap={6}
          width="100%"
          maxWidth={["100%", "100%", "900px", "900px"]}
        >
          <Input
            title="Login"
            type="number"
            name="login"
            value={formik?.values?.login}
            isDisabled={true}
            isReadOnly={true}
          />

          <Input
            title="First Name"
            type="text"
            name="f_name"
            value={formik?.values?.f_name}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            errorMessage={
              formik?.touched?.f_name && formik?.errors?.f_name
                ? formik?.errors?.f_name
                : ""
            }
          />

          <Input
            title="Middle Name"
            type="text"
            name="m_name"
            value={formik?.values?.m_name}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            errorMessage={
              formik?.touched?.m_name && formik?.errors?.m_name
                ? formik?.errors?.m_name
                : ""
            }
          />

          <Input
            title="Last Name"
            type="text"
            name="l_name"
            value={formik?.values?.l_name}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            errorMessage={
              formik?.touched?.l_name && formik?.errors?.l_name
                ? formik?.errors?.l_name
                : ""
            }
          />

          <ErrorBoundary>
            <InputWrapper
              title="Color"
              errorMessage={
                formik?.touched?.color && formik?.errors?.color
                  ? formik?.errors?.color
                  : ""
              }
            >
              <Select
                name="color"
                // @ts-ignore
                value={formik?.values?.color}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
              >
                {_.map(colors ?? [], (_c) => {
                  return <option value={_c?.key}>{_c?.title}</option>;
                })}
              </Select>
            </InputWrapper>
          </ErrorBoundary>

          <Input
            title="Email"
            type="email"
            name="email"
            value={formik?.values?.email}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            errorMessage={
              formik?.touched?.email && formik?.errors?.email
                ? formik?.errors?.email
                : ""
            }
          />

          <Input
            title="Phone"
            type="number"
            name="phone"
            value={formik?.values?.phone}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            errorMessage={
              formik?.touched?.phone && formik?.errors?.phone
                ? formik?.errors?.phone
                : ""
            }
          />

          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <Input
              title="Country"
              type="text"
              name="country"
              value={formik?.values?.country}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.country && formik?.errors?.country
                  ? formik?.errors?.country
                  : ""
              }
            />

            <Input
              title="State"
              type="text"
              name="state"
              value={formik?.values?.state}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.state && formik?.errors?.state
                  ? formik?.errors?.state
                  : ""
              }
            />

            <Input
              title="City"
              type="text"
              name="city"
              value={formik?.values?.city}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.city && formik?.errors?.city
                  ? formik?.errors?.city
                  : ""
              }
            />

            <Input
              title="Zipcode"
              type="number"
              name="zipcode"
              value={formik?.values?.zipcode}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              errorMessage={
                formik?.touched?.zipcode && formik?.errors?.zipcode
                  ? formik?.errors?.zipcode
                  : ""
              }
            />
          </Grid>

          <Input
            title="Address"
            type="text"
            name="address"
            value={formik?.values?.address}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            errorMessage={
              formik?.touched?.address && formik?.errors?.address
                ? formik?.errors?.address
                : ""
            }
          />

          <Button
            colorScheme="blue"
            type="submit"
            isLoading={formik?.isSubmitting}
          >
            Update User
          </Button>
        </Grid>
      </form>
    </>
  );
};
